import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PAGINATION_CONSTANTS } from '../../../../../../constants/pagination.constants';
import { AssetsService } from '../../../../../../services/api/generated/assets/assets.service';
import { CampaignCompletionsService } from '../../../../../../services/api/generated/campaign-completions/campaign-completions.service';
import {
  CampaignCompletion,
  CampaignInfoCompletion,
  CampaignPagination,
  GetCampaignPaginatedResourceParams,
} from '../../../../../../services/api/generated/model';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { Campaign } from '../../../../../../types/campaign.interface';
import { COMPLETED_CAMPAIGN_STATUS_FILTER } from '../../../../../../types/campaign.utils';
import { CompletionStatus } from '../../../../../../types/completion-status.enum';
import { LazyLoadParams } from '../../../../../../types/lazy-load.interface';
import { CampaignCompletionRefreshService } from './campaign-completion-refresh.service';

const STATUS_MAP: Record<number, CompletionStatus> = {
  0: CompletionStatus.WHITE,
  1: CompletionStatus.ORANGE,
  2: CompletionStatus.LIGHT_GREEN,
  3: CompletionStatus.DARK_GREEN,
  4: CompletionStatus.LOADING,
  5: CompletionStatus.ERROR,
};

const STORAGE_KEY = 'cesmon_campaign_table_preferences';

interface PuceValue {
  status: CompletionStatus;
  percentage: number;
  filled: number;
  planned: number;
  total: number;
  isLoading: boolean;
}

type PuceKey = `measureType_${number}`;

type EnrichedCampaign = Campaign & {
  [K in PuceKey]?: PuceValue;
} & {
  infoCompletion?: PuceValue;
};

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
  currentAssetId: number | null;
}

// Preferences persist the columns the user explicitly hid, so every column —
// including measure types added later or belonging to another asset — is visible by default.
interface TablePreferences {
  deselectedMeasureTypeIds: number[];
  showInfoColumn: boolean;
  showCommentColumn: boolean;
}

const initialPaginationState: PaginationState = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  limit: PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
  currentAssetId: null,
};

const toQueryParams = (params: LazyLoadParams): GetCampaignPaginatedResourceParams => ({
  page: params.pagination.page,
  pageSize: params.pagination.pageSize,
  status: COMPLETED_CAMPAIGN_STATUS_FILTER,
  ...(params.sorts?.length ? { sorts: JSON.stringify(params.sorts) } : {}),
  ...(params.filters?.length ? { filters: JSON.stringify(params.filters) } : {}),
  ...(params.globalFilter
    ? {
        globalFilter: params.globalFilter,
        ...(params.globalFilterFields?.length ? { globalFilterFields: params.globalFilterFields.join(',') } : {}),
      }
    : {}),
});

@Injectable({
  providedIn: 'root',
})
export class CompletedCampaignsGridStore {
  private readonly assetsService = inject(AssetsService);
  private readonly completionsApi = inject(CampaignCompletionsService);
  private readonly refreshService = inject(CampaignCompletionRefreshService);
  private readonly campaignStore = inject(CampaignStore);

  private readonly campaigns = signal<Campaign[]>([]);
  private readonly _paginationState = signal<PaginationState>(initialPaginationState);
  private readonly selectedMeasureTypeIds = signal<number[]>([]);
  private readonly deselectedMeasureTypeIds = signal<number[]>([]);
  private readonly showInfoColumnState = signal(true);
  private readonly showCommentColumnState = signal(true);
  private readonly backendCompletions = signal<Map<string, CampaignCompletion>>(new Map());
  private readonly backendInfoCompletions = signal<Map<number, CampaignInfoCompletion>>(new Map());
  private readonly completionsLoaded = signal(false);

  readonly paginationState = this._paginationState.asReadonly();
  readonly totalRecords = computed(() => this._paginationState().totalItems);
  readonly first = computed(() => {
    const state = this._paginationState();
    return (state.currentPage - 1) * state.limit;
  });
  readonly rows = computed(() => this._paginationState().limit);

  readonly campaignsWithPuces = computed(() => {
    const campaigns = this.campaigns();
    const measureTypes = this.selectedMeasureTypeIds();
    const showInfo = this.showInfoColumnState();
    return campaigns.map(campaign => this.enrichCampaignWithPuces(campaign, measureTypes, showInfo));
  });

  readonly showInfoColumn = this.showInfoColumnState.asReadonly();
  readonly showCommentColumn = this.showCommentColumnState.asReadonly();
  readonly selectedMeasureTypes = this.selectedMeasureTypeIds.asReadonly();
  readonly deselectedMeasureTypes = this.deselectedMeasureTypeIds.asReadonly();

  constructor() {
    this.loadPreferences();
    this.registerRefreshCallbacks();
  }

  async loadPaginatedByAssetId(assetId: number, params: LazyLoadParams): Promise<void> {
    const previousAssetId = this._paginationState().currentAssetId;
    if (previousAssetId !== null && previousAssetId !== assetId) {
      this.backendCompletions.set(new Map());
      this.backendInfoCompletions.set(new Map());
    }

    let response = await firstValueFrom(
      this.assetsService.getCampaignPaginatedResource<CampaignPagination>({ id: assetId }, toQueryParams(params)),
    );

    if (response.total > 0 && response.campaigns.length === 0 && params.pagination.page > response.pages) {
      const fallback: LazyLoadParams = { ...params, pagination: { ...params.pagination, page: Math.max(1, response.pages) } };
      response = await firstValueFrom(
        this.assetsService.getCampaignPaginatedResource<CampaignPagination>({ id: assetId }, toQueryParams(fallback)),
      );
    }

    const campaigns = response.campaigns as unknown as Campaign[];
    this.campaigns.set(campaigns);
    this._paginationState.set({
      currentPage: response.page,
      totalPages: response.pages,
      totalItems: response.total,
      limit: response.limit,
      currentAssetId: assetId,
    });

    this.completionsLoaded.set(false);
    if (campaigns.length > 0) {
      void this.loadCompletionsForCampaigns(campaigns.map(c => c.id));
    } else {
      this.completionsLoaded.set(true);
    }
  }

  setSelectedMeasureTypes(measureTypeIds: number[]): void {
    this.selectedMeasureTypeIds.set(measureTypeIds);
  }

  setDeselectedMeasureTypes(measureTypeIds: number[]): void {
    this.deselectedMeasureTypeIds.set(measureTypeIds);
    this.savePreferences();
  }

  setShowInfoColumn(show: boolean): void {
    this.showInfoColumnState.set(show);
    this.savePreferences();
  }

  setShowCommentColumn(show: boolean): void {
    this.showCommentColumnState.set(show);
    this.savePreferences();
  }

  clearPagination(): void {
    this.campaigns.set([]);
    this._paginationState.set(initialPaginationState);
    this.backendCompletions.set(new Map());
    this.backendInfoCompletions.set(new Map());
    this.completionsLoaded.set(false);
  }

  recalculatePuce(_campaignId: number, _measureTypeId: number): void {
    const assetId = this._paginationState().currentAssetId;
    if (assetId) {
      void this.loadCompletionsForAsset(assetId);
    }
  }

  async loadCompletionsForCampaigns(campaignIds: number[]): Promise<void> {
    if (campaignIds.length === 0) {
      this.completionsLoaded.set(true);
      return;
    }

    try {
      const response = await firstValueFrom(this.completionsApi.postCampaignCompletionBatchResource({ campaignIds }));
      const currentCompletions = new Map(this.backendCompletions());
      const currentInfoCompletions = new Map(this.backendInfoCompletions());

      response.completions.forEach(completion => {
        currentCompletions.set(`${completion.campaignId}-${completion.measureTypeId}`, completion);
      });

      response.campaignInfos.forEach(infoCompletion => {
        currentInfoCompletions.set(infoCompletion.campaignId, infoCompletion);
      });

      this.backendCompletions.set(currentCompletions);
      this.backendInfoCompletions.set(currentInfoCompletions);
      this.completionsLoaded.set(true);
    } catch (error) {
      console.error('Error loading batch completions:', error);
      this.completionsLoaded.set(true);
    }
  }

  private registerRefreshCallbacks(): void {
    this.refreshService.registerRefreshCallback('completed-campaigns-table-store', (campaignId: number) =>
      this.refreshCompletionForCampaign(campaignId),
    );

    this.refreshService.registerRefreshCallback('campaign-data-asset-refresh', () => this.refreshCompletionForCurrentAsset());
  }

  private enrichCampaignWithPuces(campaign: Campaign, measureTypes: number[], showInfo: boolean): EnrichedCampaign {
    const enriched: EnrichedCampaign = { ...campaign };
    const completions = this.backendCompletions();
    const infoCompletions = this.backendInfoCompletions();
    const loaded = this.completionsLoaded();

    measureTypes.forEach(measureTypeId => {
      const key = `${campaign.id}-${measureTypeId}`;
      const completion = completions.get(key);
      const measureKey: PuceKey = `measureType_${measureTypeId}`;

      if (completion) {
        enriched[measureKey] = this.createPuceValue(completion);
      } else if (loaded) {
        enriched[measureKey] = this.createWhitePuce();
      } else {
        enriched[measureKey] = this.createLoadingPuce();
      }
    });

    if (showInfo) {
      const infoCompletion = infoCompletions.get(campaign.id);

      if (infoCompletion) {
        enriched.infoCompletion = this.createPuceValue(infoCompletion);
      } else if (loaded) {
        enriched.infoCompletion = this.createWhitePuce();
      } else {
        enriched.infoCompletion = this.createLoadingPuce();
      }
    }

    return enriched;
  }

  private createLoadingPuce(): PuceValue {
    return { status: CompletionStatus.LOADING, percentage: 0, filled: 0, planned: 0, total: 0, isLoading: true };
  }

  private createWhitePuce(): PuceValue {
    return { status: CompletionStatus.WHITE, percentage: 0, filled: 0, planned: 0, total: 0, isLoading: false };
  }

  private async loadCompletionsForAsset(assetId: number): Promise<void> {
    try {
      const completions = await firstValueFrom(
        this.completionsApi.getCampaignCompletionListResource<CampaignCompletion[]>({ assetId }),
      );
      const completionMap = new Map<string, CampaignCompletion>();

      completions.forEach(completion => {
        completionMap.set(`${completion.campaignId}-${completion.measureTypeId}`, completion);
      });

      this.backendCompletions.set(completionMap);
      this.completionsLoaded.set(true);
    } catch (error) {
      console.error('Error loading backend completions:', error);
      this.completionsLoaded.set(true);
    }
  }

  private async refreshCompletionForCampaign(campaignId: number): Promise<void> {
    try {
      const campaign = this.campaignStore.byId()(campaignId);
      if (!campaign) {
        const currentCompletions = new Map(this.backendCompletions());

        for (const key of currentCompletions.keys()) {
          if (key.startsWith(`${campaignId}-`)) {
            currentCompletions.delete(key);
          }
        }

        this.backendCompletions.set(currentCompletions);
        return;
      }

      const completions = await firstValueFrom(
        this.completionsApi.getCampaignCompletionListResource<CampaignCompletion[]>({ campaignId }),
      );
      const currentCompletions = new Map(this.backendCompletions());

      completions.forEach(completion => {
        currentCompletions.set(`${completion.campaignId}-${completion.measureTypeId}`, completion);
      });

      this.backendCompletions.set(currentCompletions);
    } catch (error) {
      console.error('Error refreshing completion for campaign:', error);
    }
  }

  private async refreshCompletionForCurrentAsset(): Promise<void> {
    const assetId = this._paginationState().currentAssetId;
    if (assetId) {
      await this.loadCompletionsForAsset(assetId);
    }
  }

  private createPuceValue(completion: CampaignCompletion | CampaignInfoCompletion): PuceValue {
    return {
      status: STATUS_MAP[completion.status] ?? CompletionStatus.ERROR,
      percentage: completion.percentage,
      filled: completion.filled,
      planned: completion.planned,
      total: completion.total,
      isLoading: false,
    };
  }

  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const prefs = JSON.parse(stored) as TablePreferences;
      if (!Array.isArray(prefs.deselectedMeasureTypeIds)) {
        // Legacy entries persisted a positive selection; discard them so the grid opens with every column visible.
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      this.deselectedMeasureTypeIds.set(prefs.deselectedMeasureTypeIds);
      if (typeof prefs.showInfoColumn === 'boolean') {
        this.showInfoColumnState.set(prefs.showInfoColumn);
      }
      if (typeof prefs.showCommentColumn === 'boolean') {
        this.showCommentColumnState.set(prefs.showCommentColumn);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  private savePreferences(): void {
    const prefs: TablePreferences = {
      deselectedMeasureTypeIds: this.deselectedMeasureTypeIds(),
      showInfoColumn: this.showInfoColumnState(),
      showCommentColumn: this.showCommentColumnState(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }
}
